import React, {
  useRef, useState, ReactNode, useEffect, useCallback, useMemo, Children,
} from 'react';
import Slider from 'react-slick';
import styled from 'styled-components';
import { primaryColor } from '../../../Utils/utilsStyle';
type Props = {
  children: ReactNode;
  slidesToShow?: number;
  totalItems: number;
  NextArrow: React.ComponentType<{ onClick: () => void; color: string }>;
  PrevArrow: React.ComponentType<{ onClick: () => void; color: string }>;
  className?: string;
  onPageChange?: (currentPage: number, totalPages: number) => void;
  isCluster?: boolean;
};

const SliderWrapper = styled.div`
  width: 100%;
  position: relative;
  display: flex;
  align-items: center;

  .slider-content {
    flex: 1;
    overflow: hidden;
    margin: 0 8px;
  }

  .slick-slider {
    width: 100%;
  }

  .slick-list {
    overflow: hidden;
    margin: 0;
  }

  .slick-track {
    display: flex;
    align-items: stretch;
  }

  .slick-slide {
    height: auto;
    padding: 0 6px;
    > div {
      height: 100%;
    }
  }

  .slider-arrow {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &.disabled {
      pointer-events: none;
      opacity: 0.6;
    }
  }
  
  &.cluster-slider {
    .slick-dots {
      position: static;
      margin-top: 20px;
      bottom: auto;
      line-height: 0;

      li {
        width: 10px;
        height: 10px;
        margin: 0 5px;

        button {
          width: 10px;
          height: 10px;
          padding: 0;

          &:before {
            content: '';
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background: #CBCBCA;
            opacity: 1;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
          }
        }

        &.slick-active {
          button:before {
            width: 10px;
            height: 10px;
            background: ${primaryColor};
          }
        }
      }
    }
  }

  .empty-slide-placeholder {
    visibility: hidden;
  }
`;

const CustomSlider = function CustomSlider(props: Props) {
  const {
    children,
    slidesToShow = 4,
    totalItems,
    NextArrow,
    PrevArrow,
    className = '',
    onPageChange,
    isCluster,
  } = props;

  const sliderRef = useRef<Slider>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentSlide, setCurrentSlide] = useState(0);

  const itemsPerPage = slidesToShow;
  const totalPages = totalItems > 0 ? Math.ceil(totalItems / itemsPerPage) : 1;

  // Calculate how many empty placeholders we need to fill the last page
  const childrenWithPlaceholders = useMemo(() => {
    const childArray = Children.toArray(children);
    const remainder = totalItems % itemsPerPage;

    if (remainder === 0 || totalItems === 0) {
      return childArray;
    }

    const emptySlots = itemsPerPage - remainder;
    const placeholders = Array.from({ length: emptySlots }, (_, index) => (
      <div key={`empty-placeholder-${index}`} className="empty-slide-placeholder">
        <div style={{ height: '100%' }} />
      </div>
    ));

    return [...childArray, ...placeholders];
  }, [children, totalItems, itemsPerPage]);

  const syncClusterPageFromSlide = useCallback((slideIndex: number) => {
    setCurrentSlide(slideIndex);
    const page = Math.floor(slideIndex / itemsPerPage) + 1;
    if (onPageChange) {
      onPageChange(page, totalPages);
    }
  }, [itemsPerPage, onPageChange, totalPages]);

  // Cluster slider: reset when items or page size changes
  useEffect(() => {
    if (!isCluster) return;
    setCurrentSlide(0);
    sliderRef.current?.slickGoTo(0);
    if (onPageChange) {
      onPageChange(1, totalPages);
    }
  }, [isCluster, totalItems, itemsPerPage]);

  // Default slider: reset to page 1 if totalItems changes and current page is out of bounds
  useEffect(() => {
    if (isCluster) return;
    if (currentPage > totalPages) {
      setCurrentPage(1);
      sliderRef.current?.slickGoTo(0);
      if (onPageChange) {
        onPageChange(1, totalPages);
      }
    }
  }, [isCluster, totalItems, totalPages, currentPage, onPageChange]);

  // Default slider: notify parent of initial page on mount
  useEffect(() => {
    if (isCluster || !onPageChange) return;
    onPageChange(1, totalPages);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const settings = {
    dots: isCluster === true,
    infinite: false,
    speed: 500,
    slidesToShow: itemsPerPage,
    slidesToScroll: isCluster === true ? itemsPerPage : 1,
    arrows: false,
    swipeToSlide: true,
    ...(isCluster ? {
      afterChange: (slideIndex: number) => {
        syncClusterPageFromSlide(slideIndex);
      },
    } : {}),
  };

  const hasMultiplePages = isCluster
    ? totalItems > itemsPerPage
    : totalPages > 1;

  const isFirstPage = isCluster
    ? currentSlide === 0
    : currentPage === 1;

  const isLastPage = isCluster
    ? currentSlide + itemsPerPage >= totalItems
    : currentPage >= totalPages;

  const handleNext = useCallback(() => {
    if (isLastPage || !hasMultiplePages) return;

    if (isCluster) {
      sliderRef.current?.slickNext();
      return;
    }

    const nextPage = currentPage + 1;
    const targetSlide = (nextPage - 1) * itemsPerPage;
    sliderRef.current?.slickGoTo(targetSlide);
    setCurrentPage(nextPage);
    if (onPageChange) {
      onPageChange(nextPage, totalPages);
    }
  }, [
    currentPage,
    hasMultiplePages,
    isCluster,
    isLastPage,
    itemsPerPage,
    onPageChange,
    totalPages,
  ]);

  const handlePrev = useCallback(() => {
    if (isFirstPage || !hasMultiplePages) return;

    if (isCluster) {
      sliderRef.current?.slickPrev();
      return;
    }

    const prevPage = currentPage - 1;
    const targetSlide = (prevPage - 1) * itemsPerPage;
    sliderRef.current?.slickGoTo(targetSlide);
    setCurrentPage(prevPage);
    if (onPageChange) {
      onPageChange(prevPage, totalPages);
    }
  }, [
    currentPage,
    hasMultiplePages,
    isCluster,
    isFirstPage,
    itemsPerPage,
    onPageChange,
    totalPages,
  ]);

  // Don't render slider if no items
  if (totalItems === 0) {
    return null;
  }

  return (
    <SliderWrapper className={`custom-slider-wrapper ${isCluster ? 'cluster-slider' : ''} ${className}`}>
      <div className={`slider-arrow ${isFirstPage || !hasMultiplePages ? 'disabled' : ''}`}>
        <PrevArrow
          onClick={handlePrev}
          color='#CBCBCA'
        />
      </div>
      <div className="slider-content">
        <Slider ref={sliderRef} {...settings}>
          {childrenWithPlaceholders}
        </Slider>
      </div>
      <div className={`slider-arrow ${isLastPage || !hasMultiplePages ? 'disabled' : ''}`}>
        <NextArrow
          onClick={handleNext}
          color='#CBCBCA'
        />
      </div>
    </SliderWrapper>
  );
};

export default CustomSlider;
